<template>
  <section
    id="timeline-panel"
    role="region"
    aria-labelledby="timeline-heading"
    class="panel panel-default"
  >
    <div class="panel-body">
      <header class="panel-header">
        <h2 id="timeline-heading">Timeline</h2>
      </header>
      <p id="timeline-scroll-hint" class="sr-only">
        Your learning journey by release order, highlighting the most recently released module you are expected to be on.
        This timeline scrolls horizontally. Focus the timeline and use the arrow keys to explore all steps.
      </p>

      <p class="sr-only" v-if="currentStep">
        Step {{ currentIndex + 1 }} of {{ timelineSteps.length }}.
        {{ currentStep.name }}.
        Programme ends on {{ formatDate(timelineSteps[timelineSteps.length - 1]?.date) }}.
      </p>

      <div v-if="programmeModules?.length > 0"
        class="timeline-content"
        tabindex="0"
        aria-describedby="timeline-scroll-hint"
      >
        <ol role="list" class="timeline" aria-label="Programme progress timeline"
          :style="{ '--progress-width': progressPercentage + '%' }"
        >
          <li v-for="(step, index) in timelineSteps" :key="step.id"
            class="timeline-item"
            :class="{
              'is-current': step.isCurrent,
              'is-complete': step.isCompleted,
              'is-upcoming': step.isUpcoming,
              'is-final': step.type === 'programme',
            }"
            :aria-current="step.isCurrent ? 'step' : undefined"
          >
            <p class="sr-only">
              Step {{ index + 1 }} of {{ timelineSteps.length }}.
              {{ step.name }}.
              <span v-if="step.date && step.type === 'module'">
                Released on: {{ formatDate(step.date) }}.
              </span>
              <span v-if="step.type === 'programme'">
                Programme ends {{ formatDate(step.date) }}.
              </span>
              <span v-if="step.isCompleted">
                Completed.
              </span>
              <span v-if="step.isCurrent">
                Current step.
              </span>
            </p>

            <div class="timeline-meta">
              <p class="timeline-title">
                <span>{{ step.name }}</span>
                <span v-if="step.isCompleted" class="timeline-complete-badge">
                  <i class="fas fa-check-circle" aria-hidden="true"></i>
                  <span class="sr-only">Completed</span>
                </span>
              </p>
              <time v-if="step.date && step.type === 'module'" class="timeline-date" :datetime="step.date" aria-label="Release date">
                <i v-if="step.isUpcoming" class="far fa-clock-three" aria-hidden="true"></i>
                Starts: {{ formatDate(step.date) }}
              </time>
              <time v-if="step.date && step.type === 'programme'" class="timeline-date" :datetime="step.date" aria-label="End date"
                ><i class="fad fa-flag-checkered" aria-hidden="true"></i>  Ends: {{ formatDate(step.date) }}</time>
            </div>

            <span class="timeline-marker" aria-hidden="true"></span>

            <div class="timeline-status">
              <span v-if="step.isCurrent" class="timeline-current-label">
                You are here
              </span>
            </div>
          </li>
        </ol>
      </div>
    </div>
  </section>
</template>

<style scoped>
.timeline-content {
  position: relative;
  overflow-x: auto;
  width: 100%;
  -webkit-overflow-scrolling: touch;
}

.timeline-content:focus-visible {
  outline: 2px solid var(--kate-type-link);
  outline-offset: 2px;
}

/* =========================
    Timeline Layout
========================= */

.timeline {
  list-style: none;
  padding: 15px 0 20px;
  margin: 0;
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: 0.5fr;
  position: relative;
  min-width: max-content;
}

/* =========================
    Base Track Line
========================= */

.timeline::before {
  content: "";
  position: absolute;
  left: 0;
  right: 0;
  top: 65%;
  height: var(--line-height);
  background: var(--kate-disabled);
  transform: translateY(-50%);
  opacity: 0.5;
}

/* =========================
    Progress Line (Gradient)
========================= */

.timeline::after {
  content: "";
  position: absolute;
  left: 0;
  top: 65%;
  height: var(--line-height);
  width: var(--progress-width, 0%);
  background:
    linear-gradient(
      90deg,
      var(--kate-neon-primary-text) 0%,
      var(--kate-neon-primary-text) 60%,
      rgb(0 0 0 / 0) 100%
    );
  transform: translateY(-50%);
  z-index: 1;
  transition: width 0.4s ease;
}

/* =========================
    Timeline Item
========================= */

.timeline-item {
  position: relative;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 180px;
}

/* =========================
    Meta (Title + Dates ABOVE line)
========================= */

.timeline-meta {
  margin-bottom: 50px; /* space above line */
}

.timeline-title {
  font-weight: 500;
  margin: 0 0 0.25rem;
  font-size: 0.95rem;
  color: var(--kate-type-light) !important;
  display: inline-flex;
  align-items: center;
  gap: 5px;
}

.is-complete .timeline-title {
  color: var(--kate-type-success) !important;
}

.timeline-date {
  display: block;
  font-size: 0.8rem;
  color: #9aa4c7;
  color: var(--kate-type-alt);
}

.timeline-item.is-current .timeline-title {
  color: var(--kate-neon-primary-text) !important;
}

.timeline-item.is-upcoming .timeline-title {
  color: var(--kate-type-alt) !important;
}

.timeline-item.is-upcoming .timeline-date > i {
  color: var(--kate-type-alt);
}

.timeline-item.is-final .timeline-date > i,
.timeline-item.is-final .timeline-title {
  color: var(--kate-type-warning) !important;
}

.timeline-complete-badge {
  color: var(--kate-success);
  display: inline-flex;
  align-items: center;
}

/* =========================
    Marker
========================= */

.timeline-marker {
  position: absolute;
  top: 61%;
  left: 50%;
  width: var(--marker-size);
  height: var(--marker-size);
  background: var(--kate-track-marker-neutral);
  border: 3px solid var(--kate-background-alpha);
  box-shadow: 0 0 0 2px var(--kate-background-alpha);
  border-radius: 50%;
  transform: translate(-50%, 30%);
  z-index: 2;
  transition: all 0.3s ease;
}

.timeline-item.is-final .timeline-marker {
  background: var(--kate-warning);
  width: 30px;
  height: 30px;
  transform: translate(-50%, 0);
}

/* Completed Step */
.timeline-item.is-complete .timeline-marker {
  background: var(--kate-neon-primary-text);
}

/* Current Step */
.timeline-item.is-current .timeline-marker {
  width: 25px;
  height: 25px;
  background: var(--kate-neon-primary-text);
  box-shadow: var(--kate-track-complete-shadow);
  transform: translate(-50%, 8%);
}

/* =========================
    "You Are Here" Label BELOW
========================= */

.timeline-current-label {
  margin-top: 2.5rem;
  font-size: 0.85rem;
  color: var(--kate-neon-primary-text);
  font-weight: 500;
  display: block;
}

/* Hide label unless current */
.timeline-item:not(.is-current) .timeline-current-label {
  visibility: hidden;
}

/* =========================
    Scrollbar
========================= */

.timeline-content::-webkit-scrollbar {
  height: 8px;
}

.timeline-content::-webkit-scrollbar-thumb {
  background: rgb(255 255 255 / 0.2);
  border-radius: 4px;
}

[data-theme="light"] .timeline-content::-webkit-scrollbar-thumb {
  background: rgb(0 0 0 / 0.2);
  border-radius: 4px;
}

/* =========================
    Accessibility / Motion
========================= */

@media (prefers-reduced-motion: reduce) {
  .timeline::after,
  .timeline-marker {
    transition: none;
  }

  .timeline-marker {
    box-shadow: none;
  }
}

@media screen and (max-width: 768px) {
  .timeline-content {
    -webkit-overflow-scrolling: touch;
  }

  .timeline-item.is-current .timeline-marker {
    transform: translate(-50%, 4%);
  }

  .timeline-marker {
    transform: translate(-50%, 25%);
  }

  .timeline-item.is-final .timeline-marker {
    transform: translate(-50%, -5%);
  }
}

</style>

<script>
import useGlobalStore from '@stores/global';
import TimeMixin from '@mixins/time-mixins';
import { genericSort } from '@utils/sort-by-object-property';

export default {
  mixins: [TimeMixin],

  props: {
    learnerInfo: {
      type: Object,
      required: true,
    },
    programmeId: {
      type: Number,
      required: true,
    },
    programmeDetails: {
      type: Object,
      required: true,
    },
    programmeModules: {
      type: Array,
      default: () => [],
      required: true,
    },
  },

  data() {
    return {
      store: useGlobalStore(),
    };
  },

  mounted() {
    this.queueCenterCurrentStep();
  },

  watch: {
    currentModuleIndex() {
      this.queueCenterCurrentStep();
    },
  },

  computed: {
    orderedModules() {
      return this.programmeModules?.filter(module => module.track === 'curriculum').slice().sort(this.sortModule) || [];
    },

    currentModuleIndex() {
      if (!this.orderedModules.length) {
        return null;
      }

      const now = Date.now();
      const lastReleasedIndex = this.orderedModules.findLastIndex(module => this.hasReleased(module.release_date, now));

      return lastReleasedIndex === -1 ? null : lastReleasedIndex;
    },

    timelineSteps() {
      const modules = this.orderedModules.map((module, index) => {
        const isCurrent = this.currentModuleIndex !== null && index === this.currentModuleIndex;

        return {
          id: `module-${module.id}`,
          name: module.name,
          date: module.release_date,
          type: 'module',
          isCompleted: this.isModuleCompleted(module),
          isCurrent,
          isUpcoming: this.currentModuleIndex !== null && index > this.currentModuleIndex,
        };
      });

      return [
        ...modules,
        {
          id: `programme-${this.programmeId}`,
          name: this.programmeDetails?.name || 'Programme Complete',
          date: this.programmeDetails?.planned_end_date,
          type: 'programme',
          isCompleted: false,
          isCurrent: false,
          isUpcoming: false,
        },
      ];
    },

    currentIndex() {
      return this.currentModuleIndex ?? 0;
    },

    currentStep() {
      return this.timelineSteps.find(step => step.isCurrent) || null;
    },

    progressPercentage() {
      if (this.currentModuleIndex === null) {
        return 0;
      }

      const total = this.timelineSteps.length;
      if (total <= 1) return 0;

      const segments = total - 1;
      const segmentWidth = 100 / segments;

      const base = (this.currentIndex / segments) * 100;

      // Extend slightly beyond marker center
      const extension = segmentWidth * 1.3;

      return Math.min(base + extension, 100);
    },
  },

  methods: {
    sortModule(a, b) {
      if (a.number && b.number) {
        return genericSort(a.number, b.number);
      }
      if (!a.number && !b.number) {
        return genericSort(a.name, b.name);
      }
      return a.number ? -1 : 1;
    },

    isModuleCompleted(module) {
      if (!module) {
        return false;
      }
      if (module.is_complete === true) {
        return true;
      }
      return Number(module.percentage) >= 100;
    },

    hasReleased(date, referenceTime = Date.now()) {
      const releaseTimestamp = this.getStepTimestamp(date);
      return releaseTimestamp !== null && releaseTimestamp <= referenceTime;
    },

    queueCenterCurrentStep() {
      this.$nextTick(() => {
        // Wait for DOM/layout to settle before calculating offsets.
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this.centerCurrentStep();
          });
        });
      });
    },

    centerCurrentStep() {
      const container = this.$el.querySelector('.timeline-content');
      const current = this.$el.querySelector('.is-current');
      if (!container || !current) return;

      const offset = current.offsetLeft
        - (container.clientWidth / 2)
        + (current.clientWidth / 2);

      const reduceMotion = typeof this.prefersReducedMotion === 'function'
        ? this.prefersReducedMotion()
        : false;
      container.scrollTo({ left: offset, behavior: reduceMotion ? 'auto' : 'smooth' });
    },
    prefersReducedMotion() {
      if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
        return false;
      }
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    },

    getStepTimestamp(date) {
      if (!date) {
        return null;
      }

      const parsedDate = this.getDate(date);
      const timestamp = parsedDate?.getTime?.();

      return Number.isNaN(timestamp) ? null : timestamp;
    },

    formatDate(date) {
      if (!date) {
        return '';
      }
      return this.parseTimestamp(date, undefined, 'd MMM yyyy');
    },
  },
};
</script>
